import React, {
  useCallback,
  useEffect,
  useMemo,
  // useRef,
  useState,
} from 'react';
import { GetServerSideProps } from 'next';
import dynamic from 'next/dynamic';
import Image from 'next/image';

import config from '@/config';
import axios from 'axios';
import {
  formatCurrency,
  getLastStatusText,
  transformObjectToQuery,
  transformProperty,
} from '@/utility';
import {
  FORMAT_DATE1,
  dateDiffInDays,
  formatDate,
  fromNowInDays,
} from '@/utility/date';
import {
  AddressHistoryResponse,
  Property,
  // PropertyAddress,
  PropertyFeature,
  PropertyRoom,
  TLastStatus,
  // TPropertyType,
} from '@/types';
import AmenitiesIcon from '@/icons/amenities';
import BathIcon from '@/icons/bath';
import BedIcon from '@/icons/bed';
import BuildingIcon from '@/icons/building';
import BuiltInIcon from '@/icons/builtIn';
import SquareFitIcon from '@/icons/square-fit';
import TaxIcon from '@/icons/tax';
import UserIcon from '@/icons/user';
import clsx from 'clsx';
import {
  PropertyCard,
  PropertyCardLoader,
  PropertyContactInfo,
  PropertyFeatureCard,
  SoldDescription,
} from '@/components/property';

import Info from '@/config/info.json';
import { PropertyImageSlider } from '@/components/property/property-image-slider';
import ParkingIcon from '@/icons/parking';
import NumberFormat from '@/components/ui/react-number-format';
import { NextSeo } from 'next-seo';
import { Switch } from '@/components/ui/switch';
import IconDirection from '@/icons/icon-direction';
import { useAuth } from '@/context/auth';
import RecentPropertyService from '@/services/recent-property.service';
import AuthService from '@/services/auth.service';
import { SocialShareLinks } from '@/components/social-share-links';
import Slider from '@/components/ui/custom-slider/slider';
import useMediaBaseData from '@/hooks/use-media-base-data';
import IconNearby from '@/icons/location';
import { useSinglePropertyFavorite } from '@/context/favorite/hooks/use-favorite';
import FavoiriteIcon from '@/icons/favorite';
import FavoriteBorderIcon from '@/icons/favorite-border';
import { FavoriteUserListModal } from '@/components/modals/favorite-user-list-modal';
import CheckboxIcon from '@/icons/checkbox';
import CheckboxBlankIcon from '@/icons/checkbox-blank';
import { useCompare } from '@/context/compare/hooks/use-compare';
import Link from 'next/link';
import HightlightPreferenceSection from '@/components/property/property-detail/highlight-preference-section';
import { Button } from '@/components/ui/button';
import { ReplierListingService } from '@/services/repliers';
import NetworkService from '@/services/network.service';
import { ContactModal } from '@/components/modals';

const GPTSummary = dynamic(
  () => import('@/components/property/property-detail/gpt-summary')
);

const SuggestedPropertySummary = dynamic(
  () =>
    import('@/components/property/property-detail/suggested-property-summary')
);

export function meterToFeetAndInches(meter: number) {
  let inches = meter * 39.3701;
  const feet = Math.floor(inches / 12);
  inches = inches - feet * 12;

  return `${feet}ft' ${Math.round(inches)} in"`;
}

function getArea(length: string, width: string, checked: boolean) {
  if (!length || !width) {
    return 'Measurements not available';
  }

  if (!checked) {
    return length + ' m X ' + width + ' m';
  }

  return (
    meterToFeetAndInches(parseFloat(length)) +
    ' X ' +
    meterToFeetAndInches(parseFloat(width))
  );
}

const LazyVirtualTour = dynamic(
  () => import('@/components/property/property-virtual-tour')
);

const LazyMap = dynamic(() => import('@/components/ui/map'));

export interface PropertyDetailPageProps {
  property: Property;
}

const roomsMapping = {
  Dining: 'Dining Room',
  Living: 'Living Room',
  Kitchen: 'Kitchen',
  Br: 'Bedroom',
  '2nd Br': '2nd Bedroom',
  '3rd Br': '3rd Bedroom',
  '4th Br': '4th Bedroom',
  'Prim Bdrm': 'Primary Bedroom',
};

export default function PropertyDetailPage({
  property,
}: PropertyDetailPageProps) {
  const [skipAuthModel, setSkipAuthModel] = useState(false);
  const { loading, isAuthenticated, hasTableViewAccess } = useAuth();
  const isSold = useMemo(
    () =>
      property?.status === 'U' &&
      (property?.lastStatus === 'Sld' || property?.lastStatus === 'Lsd'),
    [property?.status, property?.lastStatus]
  );
  const blurContent = useMemo(() => {
    // skip if property not found
    if (!property) return false;

    // no blur for authenticated user
    if (isAuthenticated) return false;

    // sold, unlisted, displayPublic No, displayAddressOnInter No
    // These all should blur for non authenticated user
    if (isSold) return true;
    if (property.status === 'U') return true;
    if (property.permissions?.displayPublic === 'N') return true;
    if (property.permissions?.displayAddressOnInternet === 'N') return true;

    // Default no blur
    return false;
  }, [isAuthenticated, isSold, property]);

  useEffect(() => {
    if (loading || isAuthenticated) return;

    const authService = new AuthService();
    authService.addVisitedPropertyInQuey(property);
  }, [loading, isAuthenticated, property]);

  const openAuthModal = () => {
    const authService = new AuthService();
    authService.openAuthModel(false);
  };
  useEffect(() => {
    if (loading) return;
    if (skipAuthModel) return;

    setSkipAuthModel(true);
    if (!isAuthenticated && (isSold || blurContent)) {
      openAuthModal();
    }
  }, [loading, isAuthenticated, isSold, skipAuthModel, blurContent]);

  useEffect(() => {
    if (!property) return;
    const recentPropertyServive = new RecentPropertyService();
    recentPropertyServive.add(property);
  }, [property]);

  const count = useMediaBaseData<number>({
    xs: 1,
    sm: 1,
    md: 2,
    lg: 4,
    xl: 4,
    '2xl': 4,
    '3xl': 4,
    '4xl': 5,
    '5xl': 10,
  });

  if (!property) {
    return (
      <div className="container mx-auto px-6 pt-24 pb-60">
        <NextSeo title="Not found" />
        <h3 className="text-4xl">Oh No!</h3>
        <p className="mt-2 text-xl">
          The listing you are looking for no longer exists.
        </p>
      </div>
    );
  }

  if (isSold && loading) {
    return <SeoSection property={property} isSold={isSold} />;
  }

  const {
    mlsNumber,
    thumbnail,
    // listPrice,
    name,
    // address,
    propertyTypeString,
    propertyType,
    propertyClass,
    style: propertyStyle,
    annualTaxes,
    map,
    images,
    comparables,
    virtualTourUrl,
    nearByAmenties,
    // type,
    rooms,
    listDate,
    features,
  } = property;

  if (loading)
    return (
      <>
        <SeoSection property={property} isSold={isSold} />
        <div className="h-192" />
      </>
    );

  return (
    <>
      <SeoSection property={property} isSold={isSold} />
      <div className="container mx-auto px-6">
        <PropertyImageSlider
          blurContent={blurContent}
          images={images || []}
          title={name}
        />
        <div className="md:grid md:grid-cols-12 gap-8">
          {blurContent && (
            <div className="col-span-8">
              <BlurSection onLoginClick={openAuthModal} />
            </div>
          )}
          {!blurContent && (
            <div className="col-span-8">
              <HightlightPreferenceSection property={property} />

              <GeneralSection isSold={isSold} property={property} />

              {hasTableViewAccess && <GPTSummary property={property} />}

              <PropertySummerySection
                annualTaxes={annualTaxes}
                listDate={listDate}
                propertyClass={propertyClass}
                propertyTypeString={propertyTypeString}
                propertyType={propertyType}
                propertyStyle={propertyStyle}
                landSizeString={property.landSizeString}
              />

              <FeatureSection features={features} />

              <AmenitySection
                nearByAmenties={nearByAmenties}
                neighborhood={property.address?.neighborhood}
              />

              <RoomSection rooms={rooms} />

              <HistorySection property={property} />

              <VirtualTourSection
                virtualTourUrl={virtualTourUrl}
                thumbnail={thumbnail}
              />
              <MapSection map={map} />
              {/** TODO: Temporary commented as mortage calculator widget not working */}
              {/* <CalculatorSection
                type={type}
                address={address}
                listPrice={listPrice}
              /> */}
            </div>
          )}
          <div className="md:col-span-4 rounded-md">
            <div className="border w-full p-4 divide-y">
              <div className="flex justify-center items-center flex-col py-4">
                <div className="rounded-md">
                  <div className="h-24 relative w-24">
                    <Image
                      src={Info.ownerImage}
                      layout="fill"
                      className="rounded-full"
                      alt={Info.name}
                    />
                  </div>
                </div>

                <p className="font-medium my-2 text-xl">{Info.name}</p>
              </div>

              <div className="py-4">
                <PropertyContactInfo
                  message={`I would appreciate more information about ${name}.`}
                  isSold={isSold}
                />
              </div>
            </div>
          </div>
        </div>

        {isAuthenticated && comparables.length > 0 && (
          <div className="space-y-4 my-8" id="recommended">
            <p className="text-xl font-semibold">Recently sold comparables</p>
            <div className="flex">
              <Slider gap={32} count={count} padding={0} scrollSize={count}>
                {comparables.map((property) => (
                  <PropertyCard
                    key={property.id}
                    fullWidth
                    property={property}
                    showFavIcon
                  />
                ))}
              </Slider>
            </div>
          </div>
        )}

        <SimilarProperties mlsNumber={mlsNumber} />
      </div>
      <div className="md:hidden">
        <FloatingBookShowing isSold={isSold} name={name} />
      </div>
    </>
  );
}

function SeoSection({
  property,
  isSold,
}: {
  property: Property;
  isSold: boolean;
}) {
  const street = property.addressline1;
  let location = property.address?.city;

  let type = property.type === 'Sale' ? 'for sale' : 'for lease';

  if (isSold) {
    type = property.type === 'Sale' ? 'is sold' : 'is leased';
  }

  if (
    property.address?.neighborhood &&
    property.address?.neighborhood !== property.address?.city
  ) {
    location = `${property.address?.neighborhood}, ${property.address?.city}`;
  }

  const description = `The home ${type.toLowerCase()} at ${street} is listed for ${formatCurrency(
    property.listPrice
  )}. It has ${property.bedroomString?.toLowerCase()}, ${property.bathroomString?.toLowerCase()}, and is located in ${location}.`;

  return (
    <NextSeo
      title={property.name || 'Property Detail'}
      description={description}
      openGraph={{
        images: property.images?.map((x) => ({
          url: x + '?f=webp',
        })),
      }}
      additionalMetaTags={[
        {
          name: 'place:street_address',
          content: street,
        },
        {
          name: 'place:locality',
          content: property.address?.neighborhood,
        },
        {
          name: 'place:region',
          content: property.address?.state || property.address?.province,
        },
        {
          name: 'place:postal_code',
          content: property.address?.zip,
        },
        {
          name: 'place:location:latitude',
          content: property.map?.lat?.toString(),
        },
        {
          name: 'place:location:longitude',
          content: property.map?.lng.toString(),
        },
      ]}
    />
  );
}

function BlurSection({ onLoginClick }) {
  return (
    <div className="my-6 mx-2 relative">
      <div className="blur-sm">
        <div className="bg-gray-200 rounded h-8 w-48 mb-2"></div>
        <div className="bg-gray-200 rounded h-8 w-72 mb-2"></div>
        <div className="bg-gray-200 rounded h-8 w-96 mb-8"></div>
        <div className="bg-gray-200 rounded h-8 w-52 mb-2"></div>
        <div className="bg-gray-200 rounded h-6 mb-2"></div>
        <div className="bg-gray-200 rounded h-6 mb-2"></div>
        <div className="bg-gray-200 rounded h-6 mb-2"></div>
        <div className="grid md:grid-cols-4 my-8 gap-4">
          <div className="bg-gray-200 rounded h-16"></div>
          <div className="bg-gray-200 rounded h-16"></div>
          <div className="bg-gray-200 rounded h-16"></div>
          <div className="bg-gray-200 rounded h-16"></div>
        </div>
      </div>
      <div className="absolute inset-0 flex items-center justify-center">
        <Button onClick={onLoginClick}>Login to view content</Button>
      </div>
    </div>
  );
}

function useInstantEstimate(estimate: Property['estimate']) {
  const estimateMessage = useMemo(() => {
    if (!estimate?.value) return null;

    return (
      <div className="flex flex-row items-center sm:flex-col sm:items-end gap-1 sm:gap-0">
        <div className="text-sm">
          Instant Estimate (${formatDate(new Date(), 'MMM DD')})
        </div>
        <div className="font-bold">{formatCurrency(estimate.value)}</div>
      </div>
    );
  }, [estimate?.value]);

  return {
    estimateMessage,
  };
}

function GeneralSection({
  isSold,
  property,
}: {
  isSold: boolean;
  property: Property;
}) {
  const [currentUrl, setCurrentUrl] = useState('');
  useInstantEstimate(property.estimate);

  useEffect(() => {
    setCurrentUrl(window.location.href);
  }, []);

  const getPropertyType = useCallback(() => {
    if (isSold) {
      if (property?.lastStatus === 'Sld') {
        return 'sold';
      }
      if (property?.lastStatus === 'Lsd') {
        return 'leased';
      }
    }
    return property?.type?.toLowerCase();
  }, [property, isSold]);

  const nearByUrl = useMemo(() => {
    if (!property) return '';
    const query = {
      type: getPropertyType(),
      class: property.propertyClass?.toLowerCase(),
      refneighborhood: property.address.neighborhood?.toLowerCase(),
      view: 'map',
      zoom: 15,
      refmlsnumber: property.mlsNumber,
      lat: property.map.lat,
      lng: property.map.lng,
      reflat: property.map.lat,
      reflng: property.map.lng,
    };

    return `/properties?${transformObjectToQuery(query)}`;
  }, [property, getPropertyType]);

  const {
    mlsNumber,
    brokerageName,
    listPrice,
    soldPrice,
    areaString,
    bedroomString,
    bathroomString,
    parkingString,
    description,
    extras,
    map,
    type,
    listDate,
    lastStatus,
    soldDate,
    closedDate,
    name,
  } = property;

  const {
    isFavorite,
    isFavoriteUpdating,
    loading: isFavoriteLoading,
    handleFavoriteAction,
  } = useSinglePropertyFavorite(mlsNumber);

  const { authInfo, isAuthenticated, hasTableViewAccess } = useAuth();
  const { isCompareFn, toggleCompareItem } = useCompare();
  const isItemInCompare = isCompareFn(mlsNumber);

  const handlePropertyFavorites = () => {
    handleFavoriteAction();
  };

  const handleToggleCompare = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation();
    e.preventDefault();
    toggleCompareItem(mlsNumber);
  };

  return (
    <React.Fragment>
      <FavoriteUserListModal />
      <div className="my-6 space-y-2">
        {isSold && (
          <SoldDescription
            lastStatus={lastStatus}
            soldPrice={Number(soldPrice)}
            listPrice={listPrice}
          />
        )}
        <div className="flex flex-row justify-between items-center">
          {!isSold && (
            <span className="text-primary-400 text-2xl font-semibold">
              <NumberFormat
                value={listPrice}
                displayType={'text'}
                thousandSeparator={true}
                prefix={'$'}
              />
              {type === 'Lease' && '/mo'}
            </span>
          )}
          {isSold && (
            <span className="text-primary-400 text-2xl font-semibold">
              <NumberFormat
                value={listPrice}
                displayType={'text'}
                thousandSeparator={true}
                prefix={'$'}
                style={{ textDecoration: 'line-through' }}
                className="text-gray-500 text-xl"
              />
              &nbsp;
              <NumberFormat
                value={soldPrice}
                displayType={'text'}
                thousandSeparator={true}
                prefix={'$'}
              />
              {type === 'Lease' && '/mo'}
            </span>
          )}
          {!isSold && (
            <span className="bg-gray-500 text-white text-sm py-1 px-2 rounded bg-opacity-80">
              Listed {fromNowInDays(listDate)}
            </span>
          )}
          {isSold && !!soldDate && (
            <span className="bg-gray-500 text-white text-sm py-1 px-2 rounded bg-opacity-80">
              Sold {fromNowInDays(soldDate)}
            </span>
          )}
        </div>
        <div className="flex align-top flex-col gap-2">
          <div className="flex flex-col-reverse sm:flex-row">
            <div className="flex flex-col gap-2 flex-1">
              <div className="inline-block ">
                <span className="text-base font-semibold">{name}</span>
                {/* <br />
              <span className="text-base font-semibold">{addressline2}</span> */}
              </div>
              <p className="text-sm -mt-1">MLS® Number: {mlsNumber}</p>
            </div>
            {/* {!isSold && !!estimateMessage && (
              <div className="text-primary-500">{estimateMessage}</div>
            )} */}
          </div>
          {/* <div className="inline-block ml-8"> */}
          <div className="flex items-center flex-row flex-wrap">
            <a
              href={`https://www.google.com/maps/dir/?api=1&destination=${map.lat}%2c${map.lng}`}
              target="_blank"
              rel="noreferrer"
              className="text-primary-500 flex flex-row items-center text-sm hover:bg-gray-100 px-2 py-1 rounded"
              title="Get Direction"
            >
              <IconDirection
                className="w-6 h-6"
                style={{ display: 'inline-block' }}
              />
              &nbsp;Direction
            </a>
            <a
              href={nearByUrl}
              className="text-primary-500 flex flex-row items-center text-sm hover:bg-gray-100 px-2 py-1 rounded"
              title="Nearby Properties"
            >
              <IconNearby
                className="w-6 h-6"
                style={{ display: 'inline-block' }}
              />
              &nbsp;Nearby
            </a>

            {!isFavoriteLoading && (
              <button
                title={
                  isFavorite ? 'Remove from favourites' : 'Add to favourites'
                }
                onClick={handlePropertyFavorites}
                disabled={isFavoriteUpdating}
                className={clsx(
                  'flex flex-row items-center text-sm hover:bg-gray-100 px-2 py-1 rounded',
                  {
                    'text-primary-500': !isFavoriteUpdating,
                    'text-gray-300': isFavoriteUpdating,
                  }
                )}
              >
                {isFavorite ? (
                  <FavoiriteIcon className="w-6 h-6" />
                ) : (
                  <FavoriteBorderIcon className="w-6 h-6" />
                )}
                &nbsp;Save
              </button>
            )}
            <button
              title={
                !isItemInCompare ? 'Add to compare' : 'Remove from compare'
              }
              onClick={handleToggleCompare}
              className={clsx(
                'flex flex-row items-center text-sm hover:bg-gray-100 px-2 py-1 rounded',
                'text-primary-500'
              )}
            >
              {isItemInCompare ? (
                <CheckboxIcon className="w-6 h-6" />
              ) : (
                <CheckboxBlankIcon className="w-6 h-6" />
              )}
              &nbsp;Compare
            </button>
            <SocialShareLinks
              url={currentUrl}
              title={`Check this property ${property.name}`}
              emailSubject={`${property.mlsNumber} - ${property.name}`}
              emailBody={'Hi, Please check this property '}
            />
          </div>
        </div>
        {/* <p className="text-2xl font-semibold">{name} <a href={`https://www.google.com/maps/dir/?api=1&destination=${map.lat}%2c${map.lng}`} target="_blank" rel="noreferrer" className="text-primary-500"><IconDirection width="40" height="40" style={{ display: 'inline-block' }} /></a></p> */}
      </div>
      <hr className="-mt-4 -mb-2" />

      <div className="grid md:grid-cols-4 my-8 gap-4">
        <PropertyFeatureCard
          icon={<BedIcon className="h-5 fill-current text-primary-400" />}
          label={bedroomString}
          title="Bedrooms"
          id="bedrooms"
        />

        <PropertyFeatureCard
          icon={<BathIcon className="h-5 fill-current text-primary-400" />}
          label={bathroomString}
          title="Bathrooms"
          id="bathrooms"
        />

        <PropertyFeatureCard
          icon={<SquareFitIcon className="h-5 fill-current text-primary-400" />}
          label={areaString}
          title={'Area'}
          id="area"
        />

        <PropertyFeatureCard
          icon={<ParkingIcon className="h-5 fill-current text-primary-400" />}
          label={parkingString}
          title="Parking"
          id="parking"
        />

        {isSold && (
          <>
            <PropertyFeatureCard
              icon={
                <BuiltInIcon className="h-5 fill-current text-primary-400" />
              }
              label={formatDate(listDate, FORMAT_DATE1)}
              title="Listed on"
            />
            <PropertyFeatureCard
              icon={
                <BuiltInIcon className="h-5 fill-current text-primary-400" />
              }
              label={soldDate && formatDate(soldDate, FORMAT_DATE1)}
              title="Sold on"
            />
            <PropertyFeatureCard
              icon={
                <BuiltInIcon className="h-5 fill-current text-primary-400" />
              }
              label={
                soldDate &&
                `${Math.max(dateDiffInDays(soldDate, listDate), 0)} days`
              }
              title="Sold in"
            />
            {authInfo?.permissions.includes('fav-admin') && (
              <PropertyFeatureCard
                icon={
                  <BuiltInIcon className="h-5 fill-current text-primary-400" />
                }
                label={closedDate && formatDate(closedDate, FORMAT_DATE1)}
                title="Contract Date"
              />
            )}
          </>
        )}
      </div>

      <div className="space-y-4 my-8">
        <p className="text-xl font-semibold">Description</p>
        {isAuthenticated && hasTableViewAccess && (
          <SuggestedPropertySummary mlsNumber={property.mlsNumber} />
        )}
        <span className="text-base block leading-7 text-justify">
          {description}
          {Boolean(extras) && <> Extras:- {extras}</>}
        </span>
        <div id="agent" className="flex space-x-2 items-center">
          <UserIcon className="fill-current text-gray-300 h-4" />
          <p className="text-sm text-gray-400">Listed by {brokerageName}</p>
        </div>
      </div>
    </React.Fragment>
  );
}

function PropertySummerySection({
  // propertyTypeString,
  propertyType,
  propertyStyle,
  listDate,
  annualTaxes,
  landSizeString,
}: {
  propertyTypeString: string;
  propertyType: string;
  propertyStyle: string;
  propertyClass: string;
  listDate: string;
  annualTaxes: number;
  landSizeString: string;
}) {
  return (
    <div className="space-y-4 my-8">
      <p className="text-xl font-semibold">Property Summary</p>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <PropertyFeatureCard
          icon={<BuildingIcon className="h-5 fill-current text-primary-400" />}
          label={propertyType}
          title="Property Type"
          id="propertyType"
        />

        <PropertyFeatureCard
          icon={<BuildingIcon className="h-5 fill-current text-primary-400" />}
          label={propertyStyle}
          title="Style"
          id="style"
        />

        <PropertyFeatureCard
          icon={<BuiltInIcon className="h-5 fill-current text-primary-400" />}
          label={formatDate(listDate, FORMAT_DATE1)}
          title="Listed on"
          id="builtIn"
        />

        {Boolean(annualTaxes) && (
          <PropertyFeatureCard
            icon={<TaxIcon className="h-5 fill-current text-primary-400" />}
            label={`$${annualTaxes}`}
            title="Annual Property Taxes"
            id="tax"
          />
        )}

        {Boolean(landSizeString) && (
          <PropertyFeatureCard
            icon={
              <SquareFitIcon className="h-5 fill-current text-primary-400" />
            }
            label={landSizeString}
            title={'Land Size'}
            id="area"
          />
        )}

        {/* <PropertyFeatureCard
      icon={null}
      label={daysOnMarket}
      title="Days On Market"
      id="tax"
    /> */}
      </div>
    </div>
  );
}

function FeatureSection({ features }: { features: PropertyFeature[] }) {
  if (!features?.length) return null;

  return (
    <div className="space-y-4 my-8">
      <h4 className="text-xl font-semibold">Features</h4>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 gap-y-6">
        {features.map((feature) => (
          <PropertyFeatureCard
            key={feature.name}
            title={feature.name}
            label={
              !Array.isArray(feature.value)
                ? feature.value
                : feature.value.map((x, i) => (
                    <>
                      {i > 0 && ','}
                      {i > 0 && <br />}
                      {x}
                    </>
                  ))
            }
            bgcolor={feature.bgcolor}
            color={feature.color}
          />
        ))}
      </div>
    </div>
  );
}

function AmenitySection({
  nearByAmenties,
  neighborhood,
}: {
  nearByAmenties: string[];
  neighborhood: string;
}) {
  if (!nearByAmenties.length) return null;
  return (
    <div className="space-y-4 my-8">
      <p className="text-xl font-semibold">Neighbourhood Features</p>
      <div className="grid grid-cols-1 md:grid-cols-1 lg:grid-cols-1 gap-4">
        <PropertyFeatureCard
          icon={<AmenitiesIcon className="h-5 fill-current text-primary-400" />}
          label={neighborhood}
          title="Community Name"
        />
        <PropertyFeatureCard
          icon={<AmenitiesIcon className="h-5 fill-current text-primary-400" />}
          label={nearByAmenties.join(', ')}
          title="Nearby"
          id="amenities"
        />
      </div>
    </div>
  );
}

function RoomSection({ rooms }: { rooms: PropertyRoom[][] }) {
  const [checked, setChecked] = useState(false);

  if (!rooms.length) return null;

  return (
    <div className="space-y-4 my-8 overflow-hidden">
      <div className="flex items-center justify-between">
        <p className="text-xl font-semibold">Rooms</p>
        <div>
          <Switch
            leftLabel="Metric"
            rightLabel="Imperial"
            checked={checked}
            onChange={() => setChecked((prev) => !prev)}
          />
        </div>
      </div>
      <div className="overflow-auto">
        <table className="table-auto overflow-auto" style={{ width: '100%' }}>
          <thead>
            <tr className="bg-gray-200 bg-opacity-50">
              <th className="py-2 px-4 text-gray-800 text-left">Level</th>
              <th className="py-2 px-4 text-gray-800 text-left">Room</th>
              {/* <th className="py-2 px-4 text-gray-800">Features</th> */}
              <th className="py-2 px-4 text-gray-800 text-left">Area</th>
            </tr>
          </thead>
          <tbody>
            {rooms.map((roomData, index) => (
              <React.Fragment key={String(index)}>
                {roomData.map((element, innerIndex) => (
                  <tr
                    className={clsx({
                      'bg-gray-100 bg-opacity-50': index % 2,
                    })}
                    key={String(innerIndex)}
                  >
                    <td className="text-gray-500 py-4 px-4 whitespace-nowrap">
                      {innerIndex === 0 && (
                        <span className="block min-w-32 self-center">
                          {element.level}
                        </span>
                      )}
                    </td>
                    <td className="text-gray-500 py-4 px-4 whitespace-nowrap">
                      {roomsMapping[element.description] || element.description}
                    </td>
                    {/* <td className="text-gray-500 py-4 px-4 w-full">
                        <span className="block">
                          {element.features.join(", ")}
                        </span>
                      </td> */}
                    <td className="text-gray-500 py-4 px-4 whitespace-nowrap">
                      {getArea(element.length, element.width, checked)}
                    </td>
                  </tr>
                ))}
              </React.Fragment>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function HistorySection({ property }: { property: Property }) {
  const { isAuthenticated } = useAuth();
  const [addressHistory, setAddressHistory] =
    useState<AddressHistoryResponse | null>(null);

  const shouldShowLink = useCallback((lastStatus: TLastStatus) => {
    const whiteStatus: TLastStatus[] = ['Sld', 'Lsd', 'New'];
    return whiteStatus.includes(lastStatus);
  }, []);

  useEffect(() => {
    if (property.status === 'A' && property.history?.length) return;
    (async () => {
      try {
        const service = new NetworkService();
        const result = await service.get<AddressHistoryResponse>(
          `/api/repliers/history?${transformObjectToQuery({
            city: property.address.city,
            streetName: property.address.streetName,
            streetNumber: property.address.streetNumber,
            streetSuffix: property.address.streetSuffix,
            streetDirection: property.address.streetDirection,
            unitNumber: property.address.unitNumber,
          })}`
        );
        setAddressHistory(result);
      } catch (error) {
        console.error(error);
      }
    })();
  }, [property.address, property.status]);

  const history = useMemo(() => {
    const historyByAddress = (addressHistory?.history || []).filter(
      (x) => x.mlsNumber !== property.mlsNumber
    );
    const mergedHistory = historyByAddress.length
      ? historyByAddress
      : property.history;
    return mergedHistory.sort(
      (a, b) => new Date(b.listDate).getTime() - new Date(a.listDate).getTime()
    );
  }, [property.history, addressHistory?.history, property.mlsNumber]);

  if (!isAuthenticated) return null;
  if (!history?.length) return null;

  return (
    <div className="space-y-4 my-8 overflow-hidden">
      <div className="flex items-center justify-between">
        <p className="text-xl font-semibold">History</p>
      </div>
      <div className="overflow-auto">
        <table
          className="table-auto overflow-auto border"
          style={{ width: '100%' }}
        >
          <thead className="divide-y divide-gray-200">
            <tr className="bg-gray-100 bg-opacity-50 divide-x divide-gray-200">
              <th className="py-2 px-4 text-gray-800 text-center">
                Listing Date
              </th>
              <th className="py-2 px-4 text-gray-800 text-center">
                List Price
              </th>
              <th className="py-2 px-4 text-gray-800 text-center">
                Sold Price
              </th>
              <th className="py-2 px-4 text-gray-800 text-center">Sold Date</th>
              <th className="py-2 px-4 text-gray-800 text-center">Event</th>
              <th className="py-2 px-4 text-gray-800 text-center">
                MLS Number
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200">
            {history.map((item, index) => (
              <tr
                key={index}
                className={clsx('divide-x divide-gray-200', {
                  'bg-gray-100 bg-opacity-50': index % 2,
                })}
              >
                <td className="text-gray-500 py-2 px-4 whitespace-nowrap text-center">
                  {formatDate(item.listDate, FORMAT_DATE1)}
                </td>
                <td className="text-gray-500 py-2 px-4 whitespace-nowrap text-right">
                  <NumberFormat
                    value={item.listPrice ? Number(item.listPrice) : null}
                    displayType={'text'}
                    thousandSeparator={true}
                    prefix={'$'}
                  />
                </td>
                <td className="text-gray-500 py-2 px-4 whitespace-nowrap text-right">
                  {Boolean(item.soldPrice) && item.soldPrice !== '0.00' && (
                    <NumberFormat
                      value={item.soldPrice ? Number(item.soldPrice) : null}
                      displayType={'text'}
                      thousandSeparator={true}
                      prefix={'$'}
                    />
                  )}
                </td>
                <td className="text-gray-500 py-2 px-4 whitespace-nowrap text-center">
                  {Boolean(item.soldDate) &&
                    formatDate(item.soldDate, FORMAT_DATE1)}
                </td>
                <td className="text-gray-500 py-2 px-4 whitespace-nowrap text-center">
                  {getLastStatusText(item.lastStatus)}
                </td>
                <td className="text-gray-500 py-2 px-4 whitespace-nowrap text-center">
                  {shouldShowLink(item.lastStatus) ? (
                    <Link href={`/detail/${item.mlsNumber}/${property.slug}`}>
                      <a className="text-primary-500 hover:underline">
                        {item.mlsNumber}
                      </a>
                    </Link>
                  ) : (
                    item.mlsNumber
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function VirtualTourSection({
  virtualTourUrl,
  thumbnail,
}: {
  virtualTourUrl: string;
  thumbnail: string;
}) {
  if (!virtualTourUrl) return null;

  return (
    <div className="my-8">
      <LazyVirtualTour thumbnail={thumbnail} link={virtualTourUrl} />
    </div>
  );
}

function MapSection({ map }: { map: { lat: number; lng: number } }) {
  return (
    <div className="space-y-4 my-8">
      <p className="text-xl font-semibold">Location</p>

      <div
        id="map"
        className="h-96 w-full border rounded-sm flex justify-center items-center"
      >
        <LazyMap center={map} defaultZoom={15} />
      </div>
    </div>
  );
}

// function CalculatorSection({
//   type,
//   address,
//   listPrice,
// }: {
//   type: TPropertyType;
//   address: PropertyAddress;
//   listPrice: number;
// }) {
//   if (type !== 'Sale') return null;

//   return (
//     <div className="space-y-4 my-8">
//       <p className="text-xl font-semibold">Mortgage payment calculator</p>
//       <iframe
//         id="mortgage-calculator"
//         src={`/payment-calculator-widget?autoheight=mortgage-calculator&price=${listPrice}&city=${
//           address?.city || ''
//         }&province=${address?.province || ''}`}
//         style={{ width: '100%' }}
//       />
//     </div>
//   );
// }

function SimilarProperties(props: { mlsNumber: string }) {
  const [loading, setLoading] = useState(true);
  const [properties, setProperties] = useState<Property[]>([]);
  const count = useMediaBaseData<number>({
    xs: 1,
    sm: 1,
    md: 2,
    lg: 4,
    xl: 4,
    '2xl': 4,
    '3xl': 4,
    '4xl': 5,
    '5xl': 10,
  });

  useEffect(() => {
    setLoading(true);
    (async () => {
      const result: Property[] = await axios
        .get(
          `${config.API_URL}/api/repliers/listings/${props.mlsNumber}/similar`
        )
        .then((x) => {
          if (x.status !== 200) return null;
          return x.data.similar.map(transformProperty);
        })
        .catch(() => {
          return [];
        });

      setProperties(result);
      setLoading(false);
    })();
  }, [props.mlsNumber]);

  if (loading) {
    return (
      <div className="grid grid-col-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
        {new Array(4).fill(0).map((_, index) => (
          <PropertyCardLoader key={String(index)} />
        ))}
      </div>
    );
  }

  if (!properties.length) return null;

  return (
    <div className="space-y-4 my-8">
      <p className="text-xl font-semibold">Similar Listings</p>
      <div className="flex">
        <Slider gap={32} count={count} padding={0} scrollSize={count}>
          {properties.map((property) => (
            <PropertyCard
              key={property.id}
              fullWidth
              property={property}
              showFavIcon
            />
          ))}
        </Slider>
      </div>
    </div>
  );
}

function FloatingBookShowing({ isSold, name }) {
  const [open, setOpen] = useState(false);
  // const [showButton, setShowbutton] = useState(false);
  // const lastScrollY = useRef(0);

  // useEffect(() => {
  //   function handleScroll() {
  //     if (lastScrollY.current === window.scrollY) {
  //       return;
  //     }

  //     setShowbutton(lastScrollY.current > window.scrollY);
  //     lastScrollY.current = window.scrollY;
  //   }
  //   window.addEventListener('scroll', handleScroll);

  //   return () => window.removeEventListener('scroll', handleScroll);
  // });

  return (
    <>
      <div
        style={{
          position: 'fixed',
          // bottom: showButton ? 0 : -100,
          bottom: 0,
          left: 0,
          right: 0,
          zIndex: 49,
          transition: 'all 0.3s ease',
        }}
      >
        {/* <PropertyContactInfo
        message={`I would appreciate more information about ${'test'}.`}
        isSold={false}
      /> */}
        <div className="flex justify-center">
          <button
            className={clsx(
              'text-white font-medium text-sm flex items-center justify-center',
              'bg-primary-600 hover:bg-primary-500 w-full',
              'py-3 px-10'
            )}
            onClick={() => setOpen(true)}
          >
            {!isSold ? 'Book Showing' : 'Contact for more information'}
          </button>
        </div>
      </div>
      <ContactModal
        open={open}
        toggleModal={() => setOpen((prev) => !prev)}
        message={`I would appreciate more information about ${name}.`}
        booking
      />
    </>
  );
}

export const getServerSideProps: GetServerSideProps<
  PropertyDetailPageProps
> = async (context) => {
  let property: Property = null;

  const client = new ReplierListingService();

  try {
    const response = await client.getListing(
      context.query.id as string,
      (context.query.boardId || '') as string
    );

    if (response && !['Ter', 'Sus', 'Exp'].includes(response.lastStatus)) {
      property = transformProperty(response);
    }

    if (!property) {
      context.res.statusCode = 404;
    }

    return {
      props: {
        property,
      },
    };
  } catch (error) {
    context.res.statusCode = 404;
  }

  return {
    props: {
      property: null,
    },
  };
};
